import { strings } from "core/strings";
import { guid } from "core/types/Guid";

import { DATE_FORMATS, DateTime } from "@bps/date-time";
import { ValidationSchema, Validator } from "@bps/validator";

import { Address } from "./address";
import { Country } from "./booking";

export interface Person {
  id: guid | null;
  firstName: string;
  lastName: string;
  dateOfBirth?: DateTime;
  mobile: string;
  address: Address;
}

export const PersonMeta = {
  getDisplayName: (person: Person) =>
    `${person.firstName || ""} ${person.lastName || ""}`.trim(),
  getInitials: (person: Person) =>
    `${person.firstName[0] || ""}${person.lastName[0] || ""}`.trim(),

  // The oldest person ever to have lived was 122
  getMinDateOfBirth: () => DateTime.now().minus({ years: 125 }).startOf("day"),

  // Users must be at least 14 years old.
  getMaxDateOfBirthForUserProfile: () =>
    DateTime.now().minus({ years: 14 }).startOf("day"),

  sort: (left: Person, right: Person) => {
    const lastNameResult = strings.compare(left.lastName, right.lastName);
    return lastNameResult === 0
      ? strings.compare(left.firstName, right.firstName)
      : lastNameResult;
  }
};

const validator = new Validator<Person>();

export const PersonValidationSchema: ValidationSchema<Person> = {
  firstName: [
    validator.string().required({ message: "Please enter a first name" }),
    validator.string().pattern(/^.{2,}$/, {
      message: "The first name you have entered is too short"
    })
  ],
  lastName: [
    validator.string().required({ message: "Please enter a last name" }),
    validator.string().pattern(/^.{2,}$/, {
      message: "The last name you have entered is too short"
    })
  ]
};

export const DateOfBirthSchema = [
  validator.custom().parse<"dateOfBirth">({
    parse: (value: any) => value?.toJSDate?.() ?? value,
    then: validator
      .date()
      .isDateAfterDate(PersonMeta.getMinDateOfBirth().toJSDate(), {
        message: `The date of birth is before the minimum allowed value 
          (${PersonMeta.getMinDateOfBirth().toFormat(
            DATE_FORMATS.DAY_DEFAULT_FORMAT
          )})`
      })
  })
];

type CountryPhoneRegex = {
  [k in Country]: { regex: RegExp; errorMessage: string };
};

const PhoneValidation: CountryPhoneRegex = {
  AU: {
    regex: /^0[245]\d{8}$/,
    errorMessage:
      "Your mobile must start with '02', '04' or '05' and be 10 digits long"
  },
  NZ: {
    regex: /^0[245]\d{5,9}$/,
    errorMessage:
      "Your mobile must start with '02', '04' or '05' and not more than 10 digits long"
  }
};

// Shared phone validation logic
export const validatePhoneNumber = (
  value: string | null | undefined,
  country: Country | undefined
): string | undefined => {
  if (!value) return undefined;

  const { regex, errorMessage } =
    PhoneValidation[country || Country.NewZealand];

  if (!new RegExp(regex).test(value)) return errorMessage;
  return undefined;
};

export const MobileNumberValidationSchema = validator
  .string()
  .required({ message: "Please enter a mobile number" });

export const ValidatePhoneNumber = validator
  .custom()
  .func<"mobile">((value, parent) => {
    const error = validatePhoneNumber(
      value,
      parent?.address?.country as Country
    );
    return error ?? value;
  });
